import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "../../context/AuthContext";

export function AdminLogin() {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const { login, user, isLoading: authLoading } = useAuth();
	const [, setLocation] = useLocation();

	const [loginSuccess, setLoginSuccess] = useState(false);

	// Redirect if already logged in as super admin
	useEffect(() => {
		if (!authLoading && user && user.role === "superAdmin") {
			setLocation("/n-r-ops/dashboard");
		}
	}, [user, authLoading, setLocation]);

	// Check user role after successful login
	useEffect(() => {
		if (loginSuccess && user) {
			if (user.role === "superAdmin") {
				setLocation("/n-r-ops/dashboard");
			} else {
				// Non-admin user tried to login - show error
				setError(
					"This login is for administrators only. Please use the regular login page.",
				);
				// Clear tokens and redirect after showing error
				setTimeout(() => {
					window.location.href = "/login";
				}, 2000);
			}
			setLoginSuccess(false);
		}
	}, [loginSuccess, user, setLocation]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");

		// Validate inputs
		if (!email || !password) {
			setError("Please enter both email and password");
			return;
		}

		setIsLoading(true);

		try {
			await login(email, password);
			setLoginSuccess(true);
		} catch (err: any) {
			const errorMessage = err instanceof Error ? err.message : "Login failed";
			setError(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			{/* Admin Only Warning Banner */}
			<div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
				<div className="flex items-start gap-3">
					<AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
					<div>
						<h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200">
							Administrator Access Only
						</h3>
						<p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
							This login page is restricted to super administrators. Regular
							users should use the standard login page.
						</p>
					</div>
				</div>
			</div>

			<div>
				<label
					htmlFor="admin-email"
					className="block text-sm font-medium text-foreground mb-2"
				>
					Email
				</label>
				<input
					id="admin-email"
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
					placeholder="admin@example.com"
					autoComplete="email"
					required
				/>
			</div>

			<div>
				<label
					htmlFor="admin-password"
					className="block text-sm font-medium text-foreground mb-2"
				>
					Password
				</label>
				<input
					id="admin-password"
					type="password"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
					placeholder="Enter your password"
					autoComplete="current-password"
					required
				/>
			</div>

			{error && (
				<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
					{error}
				</div>
			)}

			<button
				type="submit"
				disabled={isLoading}
				className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md font-medium hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
			>
				{isLoading ? "Signing In..." : "Sign In as Administrator"}
			</button>
		</form>
	);
}
